import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, del, get, patch, post, postForm, put, withQuery } from "./client";

/** `/api/v1/sites`, `/api/v1/sites/{id}`, `/api/v1/sites/summary` and
 * `/api/v1/exceptions`. */

export interface SiteFilterQuery {
  q: string;
  status: string[];
  contract_id: string;
  region_id: string;
  cluster_id: string;
  provider_id: string;
  valve_make: string;
  dn_min: string;
  dn_max: string;
  assigned: string;
  located: string;
  not_located: string;
  flagged: string;
  red_list: string;
  has_qcp: string;
  no_qcp: string;
  deferred: string;
  site_type: string;
  not_done: string;
  work_order_type: string;
}

export const EMPTY_SITE_FILTER: SiteFilterQuery = {
  q: "",
  status: [],
  contract_id: "",
  region_id: "",
  cluster_id: "",
  provider_id: "",
  valve_make: "",
  dn_min: "",
  dn_max: "",
  assigned: "",
  located: "",
  not_located: "",
  flagged: "",
  red_list: "",
  has_qcp: "",
  no_qcp: "",
  deferred: "",
  site_type: "",
  not_done: "",
  work_order_type: "",
};

export function siteQuery(filter: SiteFilterQuery, base = "/api/v1/sites"): string {
  const { status, ...rest } = filter;
  const url = withQuery(base, rest);
  if (status.length === 0) {
    return url;
  }
  const statuses = status.map((s) => `status=${encodeURIComponent(s)}`).join("&");
  return url.includes("?") ? `${url}&${statuses}` : `${url}?${statuses}`;
}

/** One page of the register: how many rows, from which row. */
export interface SitePage {
  limit: number;
  offset: number;
}

/** The register's address for one page. The page travels after the filter so
 * `siteQuery` stays the filter's own address, which routing reuses. */
export function pagedSiteQuery(filter: SiteFilterQuery, page: SitePage): string {
  const url = siteQuery(filter);
  const paging = `limit=${page.limit}&offset=${page.offset}`;
  return url.includes("?") ? `${url}&${paging}` : `${url}?${paging}`;
}

export interface Flag {
  id: number;
  code: string;
  detail: string;
  blocking: boolean;
  resolved_at: string | null;
  resolution_note: string | null;
}

export interface Location {
  lat: number | null;
  lon: number | null;
  source: string | null;
  source_label: string | null;
  confidence: string | null;
}

export interface SiteRow {
  id: number;
  name: string;
  rs_no: string | null;
  /** The City's work order number, as the Red List and SAP give it. */
  notif_no: string | null;
  func_loc: string | null;
  address: string | null;
  cluster: { id: number; name: string } | null;
  status: string;
  status_label: string;
  priority: number | null;
  red_list_date: string | null;
  location: Location;
  valve_make: string | null;
  valve_desc: string | null;
  dn_scope: number | null;
  site_type: string | null;
  /** SAP's order type as the office stated it; null when nobody has. */
  work_order_type?: "PM41" | "PM42" | null;
  provider: { id: number; name: string } | null;
  deferred: boolean;
  has_qcp: boolean;
  flags: Flag[];
  /** The last planned visit -- pending, done or not done -- or null for a
   * site nobody has planned. Not done means the visit is still owed. */
  last_visit: LastVisit | null;
}

export interface LastVisit {
  outcome: "pending" | "done" | "not_done";
  plan_id: number;
  plan_name: string;
  day_index: number;
  date: string | null;
  note: string | null;
}

export interface Chip {
  field: string;
  label: string;
  value: string;
  remove_query: string;
}

/** The quick toggles' counts, keyed as the server keys them. */
export type ToggleKey = "flagged" | "red_list" | "not_located" | "no_qcp" | "deferred" | "not_done" | "unassigned";

export interface SiteRegister {
  rows: SiteRow[];
  /** Every row the filter selects; `rows` is one page of them. */
  total: number;
  limit: number;
  offset: number;
  counts: Record<string, number>;
  /** What ticking each toggle would show, under the rest of the filter. */
  toggle_counts: Record<ToggleKey, number>;
  chips: Chip[];
  filters: {
    statuses: { value: string; label: string }[];
    contracts: { id: number; name: string }[];
    regions: { id: number; name: string; contract_id: number }[];
    clusters: { id: number; name: string; region_id: number | null; contract_id: number }[];
    providers: { id: number; name: string; active: boolean }[];
  };
  office: boolean;
  exports: { xlsx: string; csv: string };
}

/** The site's pre-issued BOQ, as the office sees it on the site. */
export interface PreIssuedSummary {
  id: number;
  name: string;
  status: string;
  provisional: boolean;
  total_excl_vat: string;
  url: string;
  /** The bill is still the office's to change and no quotation is linked. */
  can_rebuild: boolean;
}

/** What building the pre-issued BOQ from the site page did. */
export interface PreIssuedBuild {
  boq_id: number;
  url: string;
  outcome: "built" | "rebuilt" | "converted";
  provisional: boolean;
  notes: string[];
}

export interface SiteDetail {
  id: number;
  name: string;
  identity: {
    facility: string | null;
    address: string | null;
    rs_no: string | null;
    func_loc: string | null;
    equip_no: string | null;
    notif_no: string | null;
    station_type: string | null;
    site_type: string | null;
  };
  asset: {
    valve_make: string | null;
    valve_desc: string | null;
    dn_scope: number | null;
    dn_boq: number | null;
    scope_type: string;
  };
  location: Location & { lo19: { y: number; x: number } | null; alt: string | null };
  cluster: { id: number; name: string } | null;
  /** The cluster's region; null while the cluster has none. */
  region: { id: number; name: string } | null;
  /** What the office may move the site to: its own contract's regions and
   * clusters. Null for whoever may not move it. */
  areas: { regions: { id: number; name: string }[]; clusters: { id: number; name: string; region_id: number | null }[] } | null;
  status: string;
  status_label: string;
  allowed_next: { value: string; label: string }[];
  provider: { id: number; name: string } | null;
  assigned_at: string | null;
  priority: number | null;
  red_list_date: string | null;
  planned_date: string | null;
  deferred: { at: string | null; reason: string | null; note: string | null } | null;
  last_visit: LastVisit | null;
  flags: Flag[];
  events: { id: number; at: string | null; by: string | null; kind: string; from: string | null; to: string | null; note: string | null }[];
  photos: { id: number; kind: string; caption: string | null; taken_at: string | null; url: string }[];
  /** Files opened rather than shown -- first, the visit sheets filed from a route plan. */
  documents: SiteDocument[];
  boq_line: { payment_ref: string; description: string; rate: string | null; qty: string; line_total: string | null } | null;
  /** The office only: absent for a crew, which never sees a price. */
  pre_issued_boq?: PreIssuedSummary | null;
  source_pdf_url: string | null;
  editable: boolean;
}

export interface SiteDocument {
  id: number;
  kind: string;
  label: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string | null;
  uploaded_by: string | null;
  url: string;
  /** The planned visit it records, when it records one. */
  visit: { plan_id: number; plan_name: string; day_index: number; date: string | null } | null;
}

export const SITES_KEY = ["sites"] as const;
export const siteKey = (id: number) => ["sites", id] as const;

/** The register under `filter`, one page at a time. The previous page stays
 * on screen while the next loads, so paging and filtering never flash an empty
 * table. */
export function useSites(filter: SiteFilterQuery, page: SitePage) {
  return useQuery<SiteRegister, ApiError>({
    queryKey: [...SITES_KEY, "register", filter, page],
    queryFn: () => get<SiteRegister>(pagedSiteQuery(filter, page)),
    placeholderData: keepPreviousData,
  });
}

/** The counts on the Sites | Exceptions | Red List strip. Under the `sites`
 * key, so every site write that refreshes the register refreshes these. */
export interface SiteSummary {
  sites: number;
  exceptions: number;
  red_list: number;
}

export function useSiteSummary() {
  return useQuery<SiteSummary, ApiError>({
    queryKey: [...SITES_KEY, "summary"],
    queryFn: () => get<SiteSummary>("/api/v1/sites/summary"),
  });
}

export function useSite(id: number | null) {
  return useQuery<SiteDetail, ApiError>({
    queryKey: siteKey(id ?? 0),
    queryFn: () => get<SiteDetail>(`/api/v1/sites/${id}`),
    enabled: id !== null,
  });
}

function useSiteWrite<TVars>(write: (vars: TVars) => Promise<SiteDetail>) {
  const client = useQueryClient();
  return useMutation<SiteDetail, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (detail) => {
      client.setQueryData(siteKey(detail.id), detail);
      void client.invalidateQueries({ queryKey: SITES_KEY });
      void client.invalidateQueries({ queryKey: ["exceptions"] });
    },
  });
}

export function useChangeStatus(id: number) {
  return useSiteWrite((body: { status: string; note: string | null }) => post<SiteDetail>(`/api/v1/sites/${id}/status`, body));
}

export function useResolveFlag(id: number) {
  return useSiteWrite((vars: { flagId: number; note: string | null }) =>
    post<SiteDetail>(`/api/v1/sites/${id}/flags/${vars.flagId}/resolve`, { note: vars.note }),
  );
}

/** Build the site's pre-issued BOQ, rebuild it from the kit, or convert the
 * worst-case draft a pilot site was committed with. */
export function useBuildPreIssuedBoq(siteId: number) {
  const client = useQueryClient();
  return useMutation<PreIssuedBuild, ApiError, void>({
    mutationFn: () => post<PreIssuedBuild>(`/api/v1/sites/${siteId}/pre-issued-boq`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: siteKey(siteId) });
      void client.invalidateQueries({ queryKey: ["boq"] });
      // A quotation's decision panel asks for the bill's reason once it exists.
      void client.invalidateQueries({ queryKey: ["quotations"] });
    },
  });
}

export function useEditIdentity(id: number) {
  return useSiteWrite((body: Partial<SiteDetail["identity"]> & { name: string; cluster_id?: number }) =>
    patch<SiteDetail>(`/api/v1/sites/${id}/identity`, body),
  );
}

export function useEditAsset(id: number) {
  return useSiteWrite((body: Partial<SiteDetail["asset"]>) => patch<SiteDetail>(`/api/v1/sites/${id}/asset`, body));
}

export function useEditLocation(id: number) {
  return useSiteWrite((body: { lat: number | null; lon: number | null }) => put<SiteDetail>(`/api/v1/sites/${id}/location`, body));
}

export function useGeocode(id: number) {
  return useSiteWrite((address: string) => post<SiteDetail>(`/api/v1/sites/${id}/location/search`, { address }));
}

export function useUploadPhoto(id: number) {
  return useSiteWrite((vars: { file: File; kind: string }) => {
    const form = new FormData();
    form.append("photo", vars.file, vars.file.name);
    form.append("kind", vars.kind);
    return postForm<SiteDetail>(`/api/v1/sites/${id}/photos`, form);
  });
}

export function useDeletePhoto(id: number) {
  return useSiteWrite((photoId: number) => del<SiteDetail>(`/api/v1/sites/${id}/photos/${photoId}`));
}

export function useDeleteDocument(id: number) {
  const client = useQueryClient();
  return useMutation<SiteDetail, ApiError, number>({
    mutationFn: (documentId) => del<SiteDetail>(`/api/v1/sites/${id}/documents/${documentId}`),
    onSuccess: (detail) => {
      client.setQueryData(siteKey(detail.id), detail);
      void client.invalidateQueries({ queryKey: SITES_KEY });
      // The plan that filed it lists it beside the visit.
      void client.invalidateQueries({ queryKey: ["routing", "plans"] });
    },
  });
}

export function useAssignSites() {
  const client = useQueryClient();
  return useMutation<{ assigned: number }, ApiError, { site_ids: number[]; provider_id: number }>({
    mutationFn: (body) => post<{ assigned: number }>("/api/v1/sites/assign", body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}

/** The Red List tick on the register: each site goes on at the next rank. */
export function useAddToRedList() {
  const client = useQueryClient();
  return useMutation<{ added: number }, ApiError, number[]>({
    mutationFn: (site_ids) => post<{ added: number }>("/api/v1/sites/redlist/add", { site_ids }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}

export function useRemoveFromRedList() {
  const client = useQueryClient();
  return useMutation<{ removed: number }, ApiError, number[]>({
    mutationFn: (site_ids) => post<{ removed: number }>("/api/v1/sites/redlist/remove", { site_ids }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}

export interface ExceptionsView {
  rows: (Flag & { site: { id: number; name: string; status: string } })[];
  open_count: number;
  codes: { code: string; count: number }[];
  blocking_codes: string[];
  active_code: string | null;
  show_resolved: boolean;
}

export function useExceptions(code: string, resolved: boolean) {
  return useQuery<ExceptionsView, ApiError>({
    queryKey: ["exceptions", { code, resolved }],
    queryFn: () => get<ExceptionsView>(withQuery("/api/v1/exceptions", { code, resolved: resolved ? "true" : "" })),
  });
}
